import { useCallback, useEffect, useMemo } from 'react';

import { Container, Flex } from '@grupoboticario/flora-react';
import { DollarSignCardIcon } from '@grupoboticario/flora-react-icons';
import { useLocation, useSearchParams } from 'react-router-dom';

import { CardSkeleton, ErrorBoundaries, ResponsiveFloraTypography } from 'components';
import { PaymentDetail, Renegotiation } from 'components/Drawers';
import config from 'config';
import { DRAWERS, ORDERS_TABS } from 'config/environments/shared';
import { useDrawer, usePaymentTitles, useTracking } from 'hooks';
import { newRelicMonitoringService } from 'services';
import { useGlobalContext } from 'store';

import { CardPayment, VdfAdvice } from './components';
import Styles from './Payments.styles';

import type { ErrorMessage } from 'domains/order';

export const Payments = () => {
  const {
    user: { isMultiBrand },
  } = useGlobalContext();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { data, isLoading, isFetching, isError, error, fetchNextPage, hasNextPage } =
    usePaymentTitles();

  const { trackWithUserData, sendDataToGTM } = useTracking();
  const { isOpen: paymentDetailIsOpen } = useDrawer(DRAWERS.PAYMENT_DETAIL);
  const { isOpen: renegotiationIsOpen } = useDrawer(DRAWERS.RENEGOTIATION);
  const paymentsData = useMemo(
    () =>
      data
        ? data?.pages.flatMap((item) => item.data).filter((item) => item.paymentStatus !== 'paid')
        : [],
    [data]
  );
  const hasEudPayment = paymentsData.some((item) => item.brand === 'EUD');
  const shouldShowVDFAdvice = !isLoading && isMultiBrand && hasEudPayment;

  const handleTrackFetchNextPage = useCallback(() => {
    fetchNextPage().then((query) => {
      if (query.isError) {
        newRelicMonitoringService.addPageAction('callback_ver_mais_pagamentos', {
          cd_interaction_detail: `erro:${query.error || 'não foi possível ver mais pagamentos'}`,
        });
      }
    });
  }, [fetchNextPage]);

  useEffect(() => {
    if (!isLoading && isError) {
      const errorResponse = error as ErrorMessage;

      newRelicMonitoringService.addPageAction('callback_a_pagar', {
        cd_interaction_detail: `erro:${errorResponse.status}`,
      });
    }

    if (!isLoading) {
      trackWithUserData({
        cd_has_orders: !!paymentsData?.length,
        ...(isError ? { cd_error_message: 'tivemos-um-problema-para-exibir-pagamentos' } : {}),
      });

      sendDataToGTM({
        event: 'page_view',
        event_parameters: {
          page_location: `${config.app.url}${pathname.substring(1)}/${
            searchParams.get('tab') || ORDERS_TABS.payments
          }`,
          page_title: 'Pedidos | A Pagar',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (isError) {
    return (
      <Styles.PaymentsWrapper data-autotagging-flora-section='pagamentos'>
        <ErrorBoundaries.ReloadPage title='Tivemos um problema para exibir os pagamentos' />
      </Styles.PaymentsWrapper>
    );
  }

  return (
    <Styles.PaymentsWrapper data-testid='payments-tab'>
      <Styles.Wrapper>
        {shouldShowVDFAdvice && <VdfAdvice />}
        <Container size='large'>
          {Boolean(!isLoading && paymentsData.length) && (
            <Styles.PaymentsSequenceInfo>
              <ResponsiveFloraTypography>Ordenado por data de vencimento</ResponsiveFloraTypography>
            </Styles.PaymentsSequenceInfo>
          )}

          <Styles.Payments aria-label='Lista de pagamentos'>
            {Boolean(!isLoading && paymentsData.length) &&
              paymentsData.map((item, index) => (
                <CardPayment
                  key={`payment-${index}-${item.orderId}`}
                  payment={item}
                  hasLogo={shouldShowVDFAdvice}
                />
              ))}
            {Boolean(isLoading || isFetching) &&
              [...Array(12)].map((_, index) => <CardSkeleton key={index} />)}

            {Boolean(!isLoading && !paymentsData.length) && (
              <Styles.WithoutPayments>
                <Container size='large'>
                  <Flex direction='column' gap='$4' align='center'>
                    <DollarSignCardIcon color='$backgroundAltPrimary' size={60} />
                    <ResponsiveFloraTypography
                      color='$nonInteractivePredominant'
                      fontSize='subtitle'
                    >
                      Você não possui pendências de pagamento
                    </ResponsiveFloraTypography>
                    <ResponsiveFloraTypography color='$nonInteractivePredominant'>
                      Ao finalizar um pedido, você poderá encontrar as informações de pagamento
                      nesta área
                    </ResponsiveFloraTypography>
                  </Flex>
                </Container>
              </Styles.WithoutPayments>
            )}
          </Styles.Payments>
        </Container>

        {Boolean(!isLoading && !isFetching && hasNextPage) && (
          <Styles.ButtonLoadMore
            disabled={isFetching}
            onClick={handleTrackFetchNextPage}
            variant='ghost'
            autotagging-flora='on'
            data-flora='button'
            data-flora-text='ver-mais-pagamentos'
          >
            <ResponsiveFloraTypography fontWeight='bold'>
              Ver mais pagamentos
            </ResponsiveFloraTypography>
          </Styles.ButtonLoadMore>
        )}
      </Styles.Wrapper>

      {paymentDetailIsOpen && <PaymentDetail />}
      {renegotiationIsOpen && <Renegotiation />}
    </Styles.PaymentsWrapper>
  );
};

export default Payments;
